import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Slider, 
  Divider,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  Select,
  InputLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RiskMatrix from './components/RiskMatrix';
import api from '../../services/api';

// Sub-component for individual Risk Assessment Cards
const RiskAssessmentCard = ({ risk, darkGlassStyle }) => {
  // Helper to map textual ratings from DB to 1-5 scale
  const mapLikelihood = (val) => {
    const map = { 'RARE': 1, 'UNLIKELY': 2, 'POSSIBLE': 3, 'LIKELY': 4, 'ALMOST CERTAIN': 5 };
    if (typeof val === 'string') return map[val.toUpperCase()] || parseInt(val) || 3;
    return parseInt(val) || 3;
  };

  const mapConsequence = (val) => {
    const map = { 'INSIGNIFICANT': 1, 'MINOR': 2, 'MODERATE': 3, 'MAJOR': 4, 'CATASTROPHIC': 5 };
    if (typeof val === 'string') return map[val.toUpperCase()] || parseInt(val) || 3;
    return parseInt(val) || 3;
  };

  // Separate states for Inherent and Residual to allow persistent "What-if" analysis
  const [inhL, setInhL] = useState(mapLikelihood(risk.inherent_likelihood));
  const [inhC, setInhC] = useState(mapConsequence(risk.inherent_consequence));
  
  const [resL, setResL] = useState(mapLikelihood(risk.residual_likelihood));
  const [resC, setResC] = useState(mapConsequence(risk.residual_consequence));

  const [activeTab, setActiveTab] = useState('inherent');

  // Currently viewed values based on active tab
  const currentL = activeTab === 'inherent' ? inhL : resL;
  const currentC = activeTab === 'inherent' ? inhC : resC;

  const setCurrentL = (val) => {
    if (activeTab === 'inherent') setInhL(val);
    else setResL(val);
  };

  const setCurrentC = (val) => {
    if (activeTab === 'inherent') setInhC(val);
    else setResC(val);
  };

  const getLikelihoodLabel = (val) => {
    const map = { 1: 'RARE', 2: 'UNLIKELY', 3: 'POSSIBLE', 4: 'LIKELY', 5: 'ALMOST CERTAIN' };
    return map[Math.round(val)] || val;
  };

  const getConsequenceLabel = (val) => {
    const map = { 1: 'INSIGNIFICANT', 2: 'MINOR', 3: 'MODERATE', 4: 'MAJOR', 5: 'CATASTROPHIC' };
    return map[Math.round(val)] || val;
  };

  const getProbability = (val) => {
    const map = { 5: '>90%', 4: '50%-90%', 3: '10%-50%', 2: '5%-10%', 1: '<5%' };
    return map[Math.round(val)] || '---';
  };

  const getRatingInfo = (l, c) => {
    const score = l * c;
    if (score >= 15) return { label: 'EXTREME', color: '#ef4444' };
    if (score >= 10) return { label: 'HIGH', color: '#f97316' };
    if (score >= 5) return { label: 'TOLERABLE', color: '#eab308' };
    if (score >= 3) return { label: 'LOW', color: '#84cc16' };
    return { label: 'VERY LOW', color: '#15803d' };
  };

  const rating = getRatingInfo(currentL, currentC);

  return (
    <Paper sx={{ ...darkGlassStyle, p: 4, mb: 4, background: 'rgba(15, 23, 42, 0.98)' }}>
      <Grid container spacing={4}>
        {/* Risk Info Header */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 'bold', letterSpacing: '1px', fontSize: '0.6rem' }}>RISK PROFILE: {risk.sn}</Typography>
              <Typography sx={{ color: '#f8fafc', fontWeight: 'bold', mt: 0.5, fontSize: '0.9rem', lineHeight: 1.4 }}>{risk.risk_description}</Typography>
              <Typography sx={{ color: '#94a3b8', mt: 0.5, fontSize: '0.75rem' }}>Category: {risk.category}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Chip label={risk.sn} sx={{ bgcolor: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', fontWeight: '900', border: '1px solid rgba(59, 130, 246, 0.4)' }} />
            </Box>
          </Box>
          <Divider sx={{ my: 3, opacity: 0.1 }} />
        </Grid>

        {/* Assessment Section */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
            <Chip 
              label="Inherent" 
              onClick={() => setActiveTab('inherent')}
              sx={{ 
                bgcolor: activeTab === 'inherent' ? '#ef4444' : 'rgba(255,255,255,0.05)',
                color: 'white', fontWeight: 'bold', flex: 1, py: 1.5, fontSize: '0.8rem'
              }}
            />
            <Chip 
              label="Residual" 
              onClick={() => setActiveTab('residual')}
              sx={{ 
                bgcolor: activeTab === 'residual' ? '#10b981' : 'rgba(255,255,255,0.05)',
                color: 'white', fontWeight: 'bold', flex: 1, py: 1.5, fontSize: '0.8rem'
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.6rem' }}>Likelihood Probability</Typography>
            <Typography sx={{ color: rating.color, fontWeight: '900', my: 1, fontSize: '0.95rem' }}>{getProbability(currentL)}</Typography>
            <Typography sx={{ color: '#f8fafc', fontWeight: 'bold', fontSize: '0.85rem' }}>{getLikelihoodLabel(currentL)}</Typography>
            <Slider 
              value={currentL} 
              min={1} max={5} 
              onChange={(e, v) => setCurrentL(v)}
              sx={{ color: rating.color, mt: 1 }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.6rem' }}>Consequence Magnitude</Typography>
            <Typography sx={{ color: '#3b82f6', fontWeight: 'bold', my: 1, fontSize: '0.85rem' }}>{getConsequenceLabel(currentC)}</Typography>
            <Slider 
              value={currentC} 
              min={1} max={5} 
              onChange={(e, v) => setCurrentC(v)}
              sx={{ color: '#3b82f6' }}
            />
          </Box>

          <Box sx={{ mt: 4, p: 1.5, background: `${rating.color}15`, borderRadius: '12px', border: `1px solid ${rating.color}30`, textAlign: 'center' }}>
            <Typography sx={{ color: rating.color, fontWeight: '900', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px' }}>{rating.label}</Typography>
          </Box>
        </Grid>

        {/* Matrix Visualization */}
        <Grid item xs={12} md={8}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
                <RiskMatrix 
                  selectedLikelihood={getLikelihoodLabel(currentL)} 
                  selectedConsequence={getConsequenceLabel(currentC)} 
                  title={`${activeTab.toUpperCase()} RISK MATRIX`} 
                />
            </Box>
            
            {/* Control Measures (Prominent on Residual) */}
            {activeTab === 'residual' && (
              <Box sx={{ mt: 2, p: 2, background: 'rgba(16, 185, 129, 0.08)', borderRadius: '12px', border: '1px dashed rgba(16, 185, 129, 0.3)' }}>
                <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 'bold', mb: 1.5, display: 'block', letterSpacing: '1px' }}>CONTROL MEASURES & MITIGATION</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 'bold' }}>EXISTING CONTROLS</Typography>
                    <Typography variant="body2" sx={{ color: '#f8fafc', mt: 0.5 }}>{risk.existing_controls || '---'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 'bold' }}>MITIGATION STRATEGY</Typography>
                    <Typography variant="body2" sx={{ color: '#f8fafc', mt: 0.5 }}>{risk.mitigation_strategy || '---'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 'bold' }}>ACTION / TREATMENT</Typography>
                    <Typography variant="body2" sx={{ color: '#f8fafc', mt: 0.5 }}>{risk.action_treatment || '---'}</Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Divider sx={{ my: 3, opacity: 0.1 }} />
            
            {/* Detailed Metadata Grid */}
            <Grid container spacing={3}>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>KRA AT RISK</Typography>
                <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: '500' }}>{risk.kra_at_risk || '---'}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>ROOT CAUSE</Typography>
                <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: '500' }}>{risk.causes || '---'}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>PRIMARY CONSEQ.</Typography>
                <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: '500' }}>{risk.consequence || '---'}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>RISK OWNER</Typography>
                <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: '500' }}>{risk.owner || '---'}</Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

const RiskAssessment = () => {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [dateReviewedFilter, setDateReviewedFilter] = useState('');
  const [targetDateFilter, setTargetDateFilter] = useState('');

  useEffect(() => {
    fetchRisks();
  }, []);

  const fetchRisks = async () => {
    try {
      const resp = await api.get('/risks');
      setRisks(resp.data);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', ...new Set(risks.map(r => r.category).filter(Boolean).sort())];

  const filteredRisks = risks.filter(r => {
    const matchesSearch = 
      r.risk_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.sn?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'All' || r.category === categoryFilter;
    const matchesReviewedDate = !dateReviewedFilter || r.date_reviewed === dateReviewedFilter;
    const matchesTargetDate = !targetDateFilter || r.resolved_by === targetDateFilter;

    return matchesSearch && matchesCategory && matchesReviewedDate && matchesTargetDate;
  });

  const darkGlassStyle = {
    background: 'rgba(15, 23, 42, 0.95)',
    backdropFilter: 'blur(30px)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    borderRadius: '24px',
    boxShadow: '0 12px 48px 0 rgba(0, 0, 0, 0.5)'
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
      <CircularProgress sx={{ color: '#10b981' }} />
    </Box>
  );

  return (
    <Box sx={{ p: 4, maxWidth: '1400px', margin: '0 auto' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: '900', color: 'white' }}>
          Risk Assessment
        </Typography>
      </Box>

      {/* Top Filter Bar */}
      <Paper sx={{ ...darkGlassStyle, p: 3, mb: 4, background: 'rgba(15, 23, 42, 0.98)' }}>
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    placeholder="Search Risk ID / Description..." 
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { color: 'white' } }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                    }}
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { color: 'white' } }}>
                    <InputLabel sx={{ color: '#94a3b8' }}>Category</InputLabel>
                    <Select
                        value={categoryFilter}
                        label="Category"
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        sx={{ '& .MuiSvgIcon-root': { color: '#94a3b8' } }}
                    >
                        {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} md={2.5}>
                <TextField 
                    fullWidth 
                    type="date"
                    label="Reviewed Date"
                    InputLabelProps={{ shrink: true, sx: { color: 'white !important' } }}
                    size="small"
                    value={dateReviewedFilter}
                    onChange={(e) => setDateReviewedFilter(e.target.value)}
                    sx={{ 
                        '& .MuiOutlinedInput-root': { color: 'white' },
                        '& .MuiInputBase-input': { color: 'white' }
                    }}
                />
            </Grid>
            <Grid item xs={12} md={2.5}>
                <TextField 
                    fullWidth 
                    type="date"
                    label="Resolution Date"
                    InputLabelProps={{ shrink: true, sx: { color: 'white !important' } }}
                    size="small"
                    value={targetDateFilter}
                    onChange={(e) => setTargetDateFilter(e.target.value)}
                    sx={{ 
                        '& .MuiOutlinedInput-root': { color: 'white' },
                        '& .MuiInputBase-input': { color: 'white' }
                    }}
                />
            </Grid>
        </Grid>
      </Paper>

      {/* Assessment Feed */}
      <Box sx={{ mt: 2 }}>
        {filteredRisks.length > 0 ? (
          filteredRisks.map(risk => (
            <RiskAssessmentCard 
                key={risk.id} 
                risk={risk} 
                darkGlassStyle={darkGlassStyle} 
            />
          ))
        ) : (
          <Paper sx={{ ...darkGlassStyle, p: 10, textAlign: 'center', background: 'rgba(15, 23, 42, 0.8)' }}>
             <Typography variant="h6" sx={{ color: '#94a3b8' }}>No assessment profiles match your current search/filters.</Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default RiskAssessment;
